import { log } from '@/log'
import { HlAppMode } from '@/models/hl-app/hl-app-mode'
import { ApiClient } from '@/services/api/client'
import { SessionApiService } from '@/services/api/session'
import { SessionApiErrorResponse } from '@/services/api/session/response'
import { SessionPayload } from '@/services/session/session-state'
import { SessionStateStore } from '@/services/session/session-state-store'

const namespace = 'sync-session'

/**
 * client側のconsoleログは収集できていないため、サーバのロギングAPIへ明示的に送る。
 *
 * LoggingService(ApiClientWithAuth)を使わず素のApiClientで送ること。
 * セッション同期の失敗はcallWithAuthの事前認証の前提が満たせない状態なので、
 * ApiClientWithAuth経由ではこのログ自体が送信できない（実行中のsyncを再帰的に待つことにもなる）。
 */
const reportErrorToLoggingApi = (message: string): void => {
  Promise.resolve(ApiClient.postJson('/api/logging', { logs: [{ level: 'error', message }] }))
    .catch(() => { /* ログ送信の失敗時にできることはない */ })
}

/**
 * サーバ上に有効なセッションが存在する状態に同期する処理。
 * sync API（有効なら延長、無ければ新規作成）を呼び、応答のstateミラーが
 * storeへ反映されていることをもって完了とする。document.cookieは読まない。
 *
 * 重複実行を避けるために成功結果をキャッシュしている。
 * その後の応答でserverが「セッション無し」を通知した場合はキャッシュを破棄し、
 * 次のexecで再同期する（失効の検知は時計ではなくサーバ応答で行う）。
 */
export class SyncSession {
  static exec(): Promise<void> {
    return SyncSession.getInstance().exec()
  }

  static onSynced<T>(callback: () => Promise<T>): Promise<T> {
    return SyncSession.getInstance().onSynced(callback)
  }

  static getInstance(): SyncSession {
    if (!SyncSession.instance) {
      SyncSession.instance = new SyncSession()
    }

    return SyncSession.instance
  }

  private static instance: SyncSession

  private syncPromise: Promise<void> | null = null
  // 失効判定（invalidateCacheIfSessionIsAbsent）は成功済みキャッシュに対してのみ行う
  private syncCompleted = false

  private constructor(
    private readonly sessionApiService: SessionApiService = new SessionApiService(),
    private readonly sessionStateStore: SessionStateStore = SessionStateStore.getInstance(),
    private readonly reportError: (message: string) => void = reportErrorToLoggingApi
  ) {}

  exec(): Promise<void> {
    this.invalidateCacheIfSessionIsAbsent()

    if (this.syncPromise) {
      return this.syncPromise
    }

    this.syncCompleted = false
    this.syncPromise = this.innerSync()
      .then((payload) => {
        this.syncCompleted = true
        log.debug({ service: `${namespace}/sync`, payload })

        return Promise.resolve()
      })

    return this.syncPromise
  }

  onSynced<T>(callback: () => Promise<T>): Promise<T> {
    return this.exec().then(callback)
  }

  private async innerSync(): Promise<SessionPayload> {
    const isHlApp = HlAppMode.isPageForHlApp || HlAppMode.isViewedInHlAppWebView
    const result = await this.sessionApiService.sync({
      isHlApp,
    })

    return result.fold(
      (error: SessionApiErrorResponse) => {
        throw new Error(`${error.errorCode}: ${error.message}`)
      },
      () => this.readSessionFromStoreOrFail()
    )
  }

  /**
   * sync成功応答のstateミラーは、APIクライアントのリスナーによってawait完了時点で
   * storeへ反映済みのはず。反映されていないのはserverとの契約違反なので、
   * リトライせずロギングAPIへ報告して失敗させる（リロードがセーフティネット）。
   */
  private readSessionFromStoreOrFail(): SessionPayload {
    const session = this.sessionStateStore.readSession()
    if (session) {
      return session
    }

    const message = `${namespace}: state mirror is not available after sync API succeeded`
      + ` (ua=${window.navigator.userAgent})`
    this.reportError(message)

    throw new Error(message)
  }

  /**
   * 前回のsync成功後に、server応答のミラーが「セッション無し」を通知していた場合、
   * キャッシュを破棄して次のexecで再同期できる状態に遷移する。
   * 例: セッション失効後の操作で、セッション前提のAPIがエラー応答と共に通知してくる。
   * 失敗したsyncPromiseのキャッシュは破棄しない（リロードをセーフティネットとする既存設計）。
   */
  private invalidateCacheIfSessionIsAbsent(): void {
    if (!this.syncPromise || !this.syncCompleted) {
      return
    }

    if (this.sessionStateStore.sessionIsAbsent()) {
      this.syncPromise = null
      this.syncCompleted = false
    }
  }
}
